/**
 * ScanLog v4 — sync driver
 *
 * Per spec §3.2. Drives sync() on:
 *   - App load (deferred 2s after first render)
 *   - `online` event (immediate kick)
 *   - `visibilitychange` → visible (immediate kick if >60s since last)
 *   - Every 30s while online + page visible
 *   - Exponential backoff ladder on failures (30s → 1m → 5m → 30m cap)
 *
 * Reactive state exposed as Svelte 5 runes so UI can show status chips.
 * Single-flight: overlapping ticks no-op until the current run settles.
 */

import { sync, countPending, type WebhookConfig, BACKOFF_LADDER_MS } from './protocol';
import { toast } from '$lib/stores/toast.svelte';
import { getMeta, setMeta } from '$db/schema';

export type SyncStatus =
	| 'idle'
	| 'syncing'
	| 'error'
	| 'offline'
	/**
	 * v4.1.6 CER-21 ER-SLx3 (HIGH): distinct state for config-incomplete
	 * (no webhook URL, no shared secret, or signed-request auth failure).
	 * Previously these collapsed to 'idle' and the UI showed "Idle, never
	 * synced" — user thought sync was working when it wasn't.
	 */
	| 'config-incomplete';

/**
 * v4.1.6 CER-21 ER-SLx4 (HIGH): translate raw protocol errors to
 * user-recoverable copy. Dev-string leakage ("HTTP 401", "push: non-JSON
 * response (<!DOCTYPE html>…)", "pull: malformed envelope") was visible
 * in the Settings sync panel; replaced with plain-language guidance.
 *
 * Input: raw error string(s) joined by '; ' from protocol.ts / signing.ts.
 * Output: { headline, detail } — headline is short + user-oriented,
 *   detail is the technical string (kept for debugging, shown in expander).
 */
export interface FriendlyError {
	headline: string;
	detail: string;
	kind:
		| 'config'
		| 'auth'
		| 'server'
		| 'network'
		| 'timeout'
		| 'unknown';
}

/**
 * v5.3.11 CCQ-327 M3 — error-code allowlist lives in protocol.ts at the
 * parse boundary (`parsePushResponse`). By the time `raw` reaches this
 * function, unknown upstream strings have already been sanitized to
 * `{"error":"internal","reqId":"..."}`. This function just maps the
 * sanitized envelope (and the other known codes) to headlines.
 */
export function friendlyError(raw: string): FriendlyError {
	const m = raw.toLowerCase();

	// v5.3.11 CCQ-327 M3: `error:internal` envelope with a reqId — surface
	// the ref id (so the user can report it) but not the underlying stack.
	// Match both JSON shape and post-parse shape that may appear in `raw`.
	if (m.includes('"error":"internal"') || m.includes('error:internal')) {
		const reqMatch = raw.match(/"reqId"\s*:\s*"([a-f0-9-]{8,})"/i);
		const reqId = reqMatch ? reqMatch[1] : null;
		return {
			headline: reqId
				? `Sheet hit an unexpected error — we've logged it (ref: ${reqId}).`
				: "Sheet hit an unexpected error — we've logged it.",
			detail: raw,
			kind: 'server'
		};
	}

	// v5.3.11 CCQ-327 M4: proxy-layer upstream failure envelope.
	if (m.includes('proxy_upstream_failure')) {
		return {
			headline: "Proxy couldn't reach the Sheet. Will retry.",
			detail: raw,
			kind: 'server'
		};
	}
	if (m.includes('proxy_not_configured')) {
		return {
			headline: 'Same-origin proxy is missing its upstream URL. Check the CF Pages env config.',
			detail: raw,
			kind: 'config'
		};
	}

	if (m.includes('no-webhook') || m.includes('webhook url not configured')) {
		return {
			headline: 'Add your Sheet webhook URL in Settings.',
			detail: raw,
			kind: 'config'
		};
	}
	if (m.includes('no-secret')) {
		return {
			headline: 'Shared secret is missing. Add it in Settings.',
			detail: raw,
			kind: 'config'
		};
	}
	// v5.3.11 CCQ-327 M1: subreason-first classifier order. Apps Script
	// emits `unauthorized: nonce replayed` / `unauthorized: ts skew` /
	// `unauthorized: clock drift`, so the generic `unauthorized` branch
	// below would swallow the subreason — sending the user to the wrong
	// "check the secret" guidance when the real fix is wait-and-retry
	// (nonce) or sync-your-clock (ts). Check the subreasons BEFORE the
	// generic auth fall-through.
	if (m.includes('nonce')) {
		return {
			headline: 'Sync request was replayed. Retrying automatically.',
			detail: raw,
			kind: 'auth'
		};
	}
	if (m.includes('ts skew') || m.includes('clock')) {
		return {
			headline: "Device clock is off — sync needs ±5 minutes of real time.",
			detail: raw,
			kind: 'auth'
		};
	}
	if (m.includes('signature') || m.includes('hmac') || m.includes('unauthorized') || m.includes('http 401') || m.includes('http 403')) {
		return {
			headline: "Sheet rejected the request — re-check the shared secret.",
			detail: raw,
			kind: 'auth'
		};
	}
	if (m.includes('non-json') || m.includes('<!doctype') || m.includes('sso')) {
		return {
			headline: 'Sheet returned an HTML page (sign-in required?). Scans are safe in the outbox.',
			detail: raw,
			kind: 'server'
		};
	}
	if (m.includes('malformed envelope')) {
		return {
			headline: 'Sheet sent an incomplete response. Will retry.',
			detail: raw,
			kind: 'server'
		};
	}
	if (m.includes('timeout')) {
		return {
			headline: 'Sheet took too long to respond. Will retry.',
			detail: raw,
			kind: 'timeout'
		};
	}
	if (m.includes('offline') || m.includes('aborted')) {
		return {
			headline: "You're offline. Scans will sync when you're back online.",
			detail: raw,
			kind: 'network'
		};
	}
	if (m.startsWith('http ')) {
		return {
			headline: 'Sheet returned an error. Will retry.',
			detail: raw,
			kind: 'server'
		};
	}
	return { headline: 'Sync hit an unexpected error. Will retry.', detail: raw, kind: 'unknown' };
}

class SyncDriver {
	status = $state<SyncStatus>('idle');
	pending = $state(0);
	lastSyncAt = $state<string | null>(null);
	lastError = $state<string | null>(null);

	private config: WebhookConfig | null = null;
	private inflight: Promise<void> | null = null;
	private timer: ReturnType<typeof setTimeout> | null = null;
	private backoffIdx = 0;
	private started = false;
	private abort: AbortController | null = null;

	/** Read webhook URL from IDB meta + any fetched prefs. */
	async loadConfig(): Promise<WebhookConfig | null> {
		const url = await getMeta<string>('webhook.url', '');
		if (!url) return null;
		const sharedSecret = await getMeta<string>('webhook.sharedSecret', '');
		this.config = { url, sharedSecret: sharedSecret || undefined };
		return this.config;
	}

	async start(): Promise<void> {
		if (this.started) return;
		this.started = true;

		// Hydrate UI state from persisted meta.
		this.pending = await countPending();
		this.lastSyncAt = await getMeta<string | null>('lastSyncAt', null);

		await this.loadConfig();
		if (typeof window !== 'undefined') {
			window.addEventListener('online', this.onOnline);
			window.addEventListener('offline', this.onOffline);
			document.addEventListener('visibilitychange', this.onVisibilityChange);
		}

		// First tick deferred 2s so first paint isn't blocked.
		this.schedule(2_000);
	}

	stop(): void {
		if (this.timer) clearTimeout(this.timer);
		this.timer = null;
		this.abort?.abort();
		this.abort = null;
		if (typeof window !== 'undefined') {
			window.removeEventListener('online', this.onOnline);
			window.removeEventListener('offline', this.onOffline);
			document.removeEventListener('visibilitychange', this.onVisibilityChange);
		}
		this.started = false;
	}

	/** Force an immediate sync (Settings "Sync now" button). */
	async kick(): Promise<void> {
		if (this.timer) clearTimeout(this.timer);
		await this.tick();
	}

	/** Reload webhook config — Settings may have changed it. */
	async reloadConfig(): Promise<void> {
		await this.loadConfig();
		this.backoffIdx = 0;
		if (this.timer) clearTimeout(this.timer);
		this.schedule(1_000);
	}

	private onOnline = () => {
		this.status = 'idle';
		this.backoffIdx = 0;
		this.kick();
	};

	private onOffline = () => {
		this.status = 'offline';
	};

	private onVisibilityChange = () => {
		if (document.hidden) return;
		// If last sync was >60s ago and we're visible again, kick immediately.
		if (!this.lastSyncAt) {
			this.kick();
			return;
		}
		const ageMs = Date.now() - new Date(this.lastSyncAt).getTime();
		if (ageMs > 60_000) this.kick();
	};

	private schedule(delayMs: number): void {
		if (this.timer) clearTimeout(this.timer);
		this.timer = setTimeout(() => this.tick(), delayMs);
	}

	private async tick(): Promise<void> {
		if (this.inflight) return; // single-flight
		this.inflight = this.run().finally(() => {
			this.inflight = null;
		});
		await this.inflight;
	}

	private async run(): Promise<void> {
		if (typeof navigator !== 'undefined' && !navigator.onLine) {
			this.status = 'offline';
			this.schedule(30_000);
			return;
		}

		// Always refresh pending count at the top of a tick for UI.
		this.pending = await countPending();

		if (!this.config) {
			await this.loadConfig();
			if (!this.config) {
				// v4.1.6 CER-21 ER-SLx3 (HIGH): surface config-incomplete state
				// instead of collapsing to 'idle' (which reads as "working fine"
				// in the UI). User needs to see that sync can't start until a
				// webhook is configured.
				this.status = 'config-incomplete';
				this.lastError = 'no-webhook';
				this.schedule(30_000);
				return;
			}
			// Config just loaded — clear any stale error.
			if (this.lastError === 'no-webhook' || this.lastError === 'no-secret') {
				this.lastError = null;
			}
		}

		this.status = 'syncing';
		this.abort = new AbortController();
		try {
			const res = await sync(this.config, this.abort.signal);

			if (res.errors.length > 0) {
				// v4.1.6 CER-21 ER-SLx3 (HIGH): soft-state classification.
				//   'no-webhook' / 'no-secret' → 'config-incomplete' (was 'idle')
				//   'offline' → 'offline' (unchanged)
				// Prior code collapsed config errors to 'idle' which misled user.
				const hasOffline = res.errors.includes('offline');
				const hasConfig = res.errors.some(
					(e) => e === 'no-webhook' || e === 'no-secret'
				);
				const allSoft = res.errors.every(
					(e) => e === 'no-webhook' || e === 'no-secret' || e === 'offline'
				);
				if (allSoft) {
					if (hasOffline) {
						this.status = 'offline';
						this.lastError = null;
					} else if (hasConfig) {
						this.status = 'config-incomplete';
						this.lastError = res.errors.join('; ');
					}
					this.schedule(30_000);
				} else {
					this.status = 'error';
					this.lastError = res.errors.join('; ');
					const delay =
						BACKOFF_LADDER_MS[
							Math.min(this.backoffIdx, BACKOFF_LADDER_MS.length - 1)
						] ?? BACKOFF_LADDER_MS[BACKOFF_LADDER_MS.length - 1] ?? 0;
					this.backoffIdx = Math.min(this.backoffIdx + 1, BACKOFF_LADDER_MS.length - 1);
					this.schedule(delay);
				}
				return;
			}

			// Success — clear error state, reset backoff, record time, refresh pending.
			this.status = 'idle';
			this.lastError = null;
			this.backoffIdx = 0;
			const now = new Date().toISOString();
			this.lastSyncAt = now;
			await setMeta('lastSyncAt', now);
			this.pending = await countPending();

			// §2.9 P2-1 conflict toast — exactly one toast per cycle, summarizing count.
			if (res.conflicts > 0) {
				toast.push({
					kind: 'info',
					text:
						res.conflicts === 1
							? '1 item updated from server'
							: `${res.conflicts} items updated from server`,
					duration: 4000
				});
			}

			// If we pushed a full batch, there may be more — go again sooner.
			if (res.pushed >= 50) {
				this.schedule(2_000);
			} else {
				this.schedule(30_000);
			}
		} catch (err) {
			this.status = 'error';
			this.lastError = err instanceof Error ? err.message : String(err);
			const delay =
				BACKOFF_LADDER_MS[
					Math.min(this.backoffIdx, BACKOFF_LADDER_MS.length - 1)
				] ?? BACKOFF_LADDER_MS[BACKOFF_LADDER_MS.length - 1] ?? 0;
			this.backoffIdx = Math.min(this.backoffIdx + 1, BACKOFF_LADDER_MS.length - 1);
			this.schedule(delay);
		} finally {
			this.abort = null;
		}
	}
}

export const syncDriver = new SyncDriver();
